import classNames from "classnames"
import React, { PropsWithChildren } from "react"

import GatsbyStoryblokImage from "../../elements/V2/GatsbyStoryblokImage"

import Link from "@components/elements/Link"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import Section from "@components/elements/V2/Section"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

type Props = PropsWithChildren<{
  title: string
  publicationInfo?: string
  ctaText?: string
  ctaLink?: string
  socialLinks: Array<Storyblok.SocialLink>
  backgroundColor?: Color
}>

const ArticleSection = ({
  title,
  publicationInfo,
  ctaText,
  ctaLink,
  socialLinks,
  backgroundColor = Color.White,
  children,
}: Props) => {
  return (
    <Section
      className={classNames(
        "rounded-t-lg pb-lg -mb-lg relative",
        getBackgroundColorClass(backgroundColor)
      )}
    >
      <div className="grid w-full grid-cols-1 grid-rows-[auto] pt-24 sm:grid-cols-[1fr_2fr]">
        <div className="flex h-fit items-center gap-20 pb-24 sm:pb-0">
          {ctaText && ctaLink && (
            <PillButton
              style="solid"
              size="large"
              color={Color.Blue}
              text={ctaText}
              linkUrl={ctaLink}
            />
          )}
          {socialLinks &&
            socialLinks.map((link: Storyblok.SocialLink, index: number) => (
              <Link link={link.url} key={index}>
                <div className="flex h-[36px] w-[36px] items-center justify-center rounded-12 bg-charcoal/5">
                  <GatsbyStoryblokImage
                    image={link.icon.filename}
                    alt={link.icon.alt}
                  />
                </div>
              </Link>
            ))}
        </div>
        {publicationInfo && (
          <div className="flex items-center py-8 lg:py-16">
            <Typography
              text={publicationInfo}
              size="body-sm"
              font="grotesk"
              weight="medium"
              color={getAccentColor(backgroundColor)}
            />
          </div>
        )}

        <div
          className={classNames(
            publicationInfo
              ? "py-8 sm:py-16 lg:py-32"
              : "pb-8 sm:pb-16 lg:pb-32",
            "flex w-full flex-col gap-20 sm:col-start-2 sm:gap-24 lg:gap-32"
          )}
        >
          <Typography
            text={title}
            size="lead-lg"
            font="grotesk"
            weight="medium"
            color={getAccentColor(backgroundColor)}
          />
          {children && (
            <div className="flex flex-col gap-20 sm:gap-24 lg:gap-32">
              {children}
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}

export default ArticleSection
